/**
 * Projects API
 *
 * All project-related data access functions.
 * Uses RLS policies for authorization.
 */

import { supabase, normalizeError } from '@/lib/supabaseClient'

// SECURITY: Input validation and sanitization helpers
function sanitizeSearchString(input) {
  if (!input || typeof input !== 'string') return ''
  // Limit length to prevent abuse
  const maxLength = 100
  let sanitized = input.slice(0, maxLength).trim()
  // Escape SQL LIKE special characters
  sanitized = sanitized.replace(/[%_]/g, '\\$&')
  return sanitized
}

function validateNumericFilter(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
  if (value === null || value === undefined) return null
  const num = Number(value)
  if (isNaN(num)) return null
  return Math.max(min, Math.min(num, max))
}

/**
 * Search projects using full-text search
 *
 * @param {string} searchQuery - Search query string
 * @param {number} maxResults - Maximum number of results (default: 20)
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function searchProjects(searchQuery, maxResults = 20) {
  try {
    // SECURITY: Validate and sanitize search query
    const sanitizedQuery = sanitizeSearchString(searchQuery)
    if (!sanitizedQuery) {
      return { data: [], error: null }
    }

    // SECURITY: Validate maxResults
    const validatedMaxResults = validateNumericFilter(maxResults, 1, 100) || 20

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase.rpc('search_projects', {
      search_query: sanitizedQuery,
      max_results: validatedMaxResults,
    })

    if (error) throw error

    let followedProjectIds = new Set()
    if (user) {
      const { data: follows } = await supabase
        .from('follows')
        .select('project_id')
        .eq('user_id', user.id)

      followedProjectIds = new Set(follows?.map(f => f.project_id) || [])
    }

    const enrichedData = await Promise.all(
      (data || []).map(async (project) => {
        const { data: developer } = await supabase
          .from('profiles')
          .select('id, full_name, company_name')
          .eq('id', project.developer_id)
          .single()

        const { data: discountData } = await supabase.rpc('get_current_discount_for_project', {
          p_project_id: project.id,
        })

        return {
          ...project,
          developer,
          is_following: followedProjectIds.has(project.id),
          current_discount: discountData?.[0] || null
        }
      })
    )

    return { data: enrichedData, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch all published projects with optional filters
 *
 * @param {Object} filters - Filter options
 * @param {string} filters.location - Location filter
 * @param {number} filters.minPrice - Minimum price
 * @param {number} filters.maxPrice - Maximum price
 * @param {number} filters.bedrooms - Number of bedrooms
 * @param {string} filters.status - Project status
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchProjects(filters = {}) {
  try {
    const { data: { user } } = await supabase.auth.getUser()

    let query = supabase
      .from('projects')
      .select(`
        *,
        developer:profiles!developer_id(id, full_name, company_name),
        units(bedrooms, bathrooms, price, status)
      `)
      .in('status', ['activated', 'published', 'coming_soon'])
      .order('created_at', { ascending: false })

    // Apply filters with validation and sanitization
    if (filters.location) {
      const sanitizedLocation = sanitizeSearchString(filters.location)
      if (sanitizedLocation) {
        query = query.ilike('location', `%${sanitizedLocation}%`)
      }
    }

    const minPrice = validateNumericFilter(filters.minPrice, 0, 100000000)
    if (minPrice !== null) {
      query = query.gte('price_range_min', minPrice)
    }

    const maxPrice = validateNumericFilter(filters.maxPrice, 0, 100000000)
    if (maxPrice !== null) {
      query = query.lte('price_range_max', maxPrice)
    }

    if (filters.status) {
      // Whitelist valid statuses
      const validStatuses = ['draft', 'activated', 'published', 'coming_soon', 'selling', 'sold_out']
      if (validStatuses.includes(filters.status)) {
        query = query.eq('status', filters.status)
      }
    }

    const { data, error } = await query

    if (error) throw error

    let followedProjectIds = new Set()
    if (user) {
      const { data: follows } = await supabase
        .from('follows')
        .select('project_id')
        .eq('user_id', user.id)

      followedProjectIds = new Set(follows?.map(f => f.project_id) || [])
    }

    const enrichedData = await Promise.all(
      data.map(async (project) => {
        const { data: discountData } = await supabase.rpc('get_current_discount_for_project', {
          p_project_id: project.id,
        })

        return {
          ...project,
          is_following: followedProjectIds.has(project.id),
          current_discount: discountData?.[0] || null
        }
      })
    )

    return { data: enrichedData, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch single project by ID with all details
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function fetchProjectById(projectId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        developer:profiles!developer_id(id, full_name, company_name, email, phone),
        units(
          id,
          unit_number,
          bedrooms,
          bathrooms,
          sqm,
          price,
          status,
          floor_plan_url
        )
      `)
      .eq('id', projectId)
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Fetch projects created by current developer
 *
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchMyProjects() {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) throw new Error('Not authenticated')

    const { data: projects, error } = await supabase
      .from('projects')
      .select('*')
      .eq('developer_id', user.id)
      .order('created_at', { ascending: false })

    if (error) throw error

    const enrichedData = await Promise.all(
      (projects || []).map(async (project) => {
        const { data: units } = await supabase
          .from('units')
          .select('id, status')
          .eq('project_id', project.id)

        const totalUnits = units?.length || 0
        const draftUnits = units?.filter(u => u.status === 'draft').length || 0
        const availableUnits = units?.filter(u => u.status === 'available').length || 0
        const reservedUnits = units?.filter(u => u.status === 'reserved').length || 0
        const soldUnits = units?.filter(u => u.status === 'sold').length || 0

        return {
          ...project,
          units_count: totalUnits,
          draft_units_count: draftUnits,
          available_units_count: availableUnits,
          reserved_units_count: reservedUnits,
          sold_units_count: soldUnits,
        }
      })
    )

    return { data: enrichedData, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Create a new project (developer only)
 *
 * @param {Object} projectData - Project data
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function createProject(projectData) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('projects')
      .insert({
        ...projectData,
        developer_id: user.id,
      })
      .select()
      .single()

    if (error) throw error

    await supabase.rpc('log_activity', {
      p_action_type: 'project_created',
      p_resource_type: 'project',
      p_resource_id: data.id,
      p_description: `Created project: ${data.name}`,
      p_metadata: { location: data.location }
    })

    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Update existing project (developer only, own projects)
 *
 * @param {string} projectId - Project UUID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{data: Object|null, error: Object|null}>}
 */
export async function updateProject(projectId, updates) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Delete project (developer only, own projects)
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function deleteProject(projectId) {
  try {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Follow a project (buyer only)
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function followProject(projectId) {
  try {
    const { error } = await supabase.rpc('follow_project', {
      p_project_id: projectId,
    })

    if (error) throw error

    const { data: project } = await supabase
      .from('projects')
      .select('name')
      .eq('id', projectId)
      .maybeSingle()

    await supabase.rpc('log_activity', {
      p_action_type: 'follow_project',
      p_resource_type: 'project',
      p_resource_id: projectId,
      p_description: `Started following ${project?.name || 'project'}`,
      p_metadata: {}
    })

    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Unfollow a project (buyer only)
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{error: Object|null}>}
 */
export async function unfollowProject(projectId) {
  try {
    const { error } = await supabase.rpc('unfollow_project', {
      p_project_id: projectId,
    })

    if (error) throw error
    return { error: null }
  } catch (error) {
    return { error: normalizeError(error) }
  }
}

/**
 * Fetch projects followed by current buyer
 *
 * @returns {Promise<{data: Array|null, error: Object|null}>}
 */
export async function fetchFollowedProjects() {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('follows')
      .select(`
        id,
        created_at,
        project:projects(
          id,
          name,
          location,
          price_range_min,
          price_range_max,
          status,
          image_urls,
          developer:profiles!developer_id(full_name, company_name)
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Check if current user is following a project
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: boolean, error: Object|null}>}
 */
export async function isFollowingProject(projectId) {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) return { data: false, error: null }

    const { data, error } = await supabase
      .from('follows')
      .select('id')
      .eq('project_id', projectId)
      .eq('user_id', user.id)
      .maybeSingle()

    if (error) throw error
    return { data: !!data, error: null }
  } catch (error) {
    return { data: false, error: normalizeError(error) }
  }
}

/**
 * Get follower count for a project
 *
 * @param {string} projectId - Project UUID
 * @returns {Promise<{data: number, error: Object|null}>}
 */
export async function getFollowerCount(projectId) {
  try {
    const { count, error } = await supabase
      .from('follows')
      .select('*', { count: 'exact', head: true })
      .eq('project_id', projectId)

    if (error) throw error
    return { data: count || 0, error: null }
  } catch (error) {
    return { data: 0, error: normalizeError(error) }
  }
}
